import { Slide, Typography } from "@mui/material";
import _, { debounce } from "lodash";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import Button from "../../../framework/Button";
import GlobalContext from "../../../framework/GlobalContext";
import Shortcut from "../../../framework/Shortcut";
import Tab from "../../../framework/tab/Tab";
import {
  ADD_PRODUCT_GENERAL_TAB,
  ADD_PRODUCT_MISCELLANEOUS_TAB,
  ADD_PRODUCT_STOCK_OR_PRICES_TAB,
} from "../../../framework/tab/tabUtilsConstants";
import { displayToast } from "../../../framework/toast/toastUtils";
import { getCategory } from "../../../services/category-services";
import {
  getMedicineDetails,
  getPharmaProVariables,
  saveMedicine,
} from "../../../services/medicine-services";
import {
  checkErrorStateExists,
  cleanObject,
  focusInputField,
  removeErrorState,
} from "../../../utils/utils";
import AddIngredients from "../AddIngredients";
import { createFormState } from "./addProductUtils";
import classes from "./index.module.css";

const AddProduct = ({
  isProductSearchable = true,
  onSave = () => {},
  showAddProduct,
  newProduct = null,
  onEscape = () => {},
  editProductId = null,
}) => {
  const dispatch = useDispatch();
  const { setShowAddProductAutocomplete, isAddRecipe, setCurrentComponent } =
    useContext(GlobalContext);

  const [focusTab, setFocusTab] = useState(0);
  const [isAddIngredients, setIsAddIngredients] = useState(false);
  const [ingredients, setIngredients] = useState([]);
  const [path, setPath] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [productDetails, setProductDetails] = useState({
    medicineId: "",
    medicineName: "",
  });

  const addProductButtonRef = useRef();
  const saveRecipeButtonRef = useRef();
  const addIngredientsButtonRef = useRef();
  const backButtonRef = useRef();
  const categoryRef = useRef();

  const { formState, errorState, categoryPath } = useSelector(
    (state) => state.commonReducer.common.addProduct
  );

  const showDeleteIcon = useMemo(() => {
    if (ingredients?.length === 1) {
      return false;
    }
    return true;
  }, [ingredients?.length]);

  const fetchData = useCallback(async () => {
    try {
      const response = await getPharmaProVariables();
      if (
        response.status === "success" &&
        response.result?.collection_meta?.product?.last_added_info
      ) {
        const { medicineId, medicineName } =
          response.result.collection_meta.product.last_added_info;
        setProductDetails({
          medicineId,
          medicineName,
        });
      } else {
        console.error("Error: Medicine details not found in response");
      }
    } catch (error) {
      console.error("Error fetching data: ", error);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);
  const resetIngredients = useCallback(() => {
    setIngredients([]);
  }, []);

  const updateErrorStates = useCallback(
    (errorState) => {
      dispatch({
        type: "set_add_product_error_state",
        payload: errorState,
      });
    },
    [dispatch]
  );

  const validateForm = useCallback(
    (fieldName, data) => {
      const _formState = { ...data };
      let updatedErrorState = errorState;
      if (!fieldName || fieldName === "add_product_name") {
        if (!_formState?.productName) {
          if (!checkErrorStateExists("add_product_name", errorState)) {
            updatedErrorState.push({ add_product_name: true });
          }
          updateErrorStates(updatedErrorState);
          if (!fieldName) {
            focusInputField(updatedErrorState);
          }
          displayToast({
            type: "error",
            title: "Please enter a medicine name",
          });
          return false;
        } else {
          if (checkErrorStateExists("add_product_name", errorState)) {
            updatedErrorState = removeErrorState(
              "add_product_name",
              updatedErrorState,
              errorState
            );
            updateErrorStates(updatedErrorState);
          }
        }
      }
      if (!fieldName || fieldName === "add_product_type") {
        if (!_formState?.itemType?.label) {
          if (!checkErrorStateExists("add_product_type", errorState)) {
            updatedErrorState.push({ add_product_type: true });
          }
          updateErrorStates(updatedErrorState);
          focusInputField("add_product_type");
          displayToast({
            type: "error",
            title: "Please enter a valid item type",
          });
          return false;
        } else {
          if (checkErrorStateExists("add_product_type", errorState)) {
            updatedErrorState = removeErrorState(
              "add_product_type",
              updatedErrorState,
              errorState
            );
            updateErrorStates(updatedErrorState);
          }
        }
      }
      if (!fieldName || fieldName === "add_product_category") {
        if (!_formState?.category?.value?.name) {
          if (!checkErrorStateExists("add_product_category", errorState)) {
            updatedErrorState.push({ add_product_category: true });
          }
          updateErrorStates(updatedErrorState);
          focusInputField("add_product_category");
          displayToast({
            type: "error",
            title: "Please select a category",
          });
          return false;
        } else {
          if (checkErrorStateExists("add_product_category", errorState)) {
            updatedErrorState = removeErrorState(
              "add_product_category",
              updatedErrorState,
              errorState
            );
            updateErrorStates(updatedErrorState);
          }
        }
      }
      if (!fieldName || fieldName === "add_product_packaging") {
        if (!_formState?.medicinePackaging) {
          if (!checkErrorStateExists("add_product_packaging", errorState)) {
            updatedErrorState.push({ add_product_packaging: true });
          }
          updateErrorStates(updatedErrorState);
          focusInputField("add_product_packaging");
          displayToast({
            type: "error",
            title: "Please enter a packaging",
          });
          return false;
        } else {
          if (checkErrorStateExists("add_product_packaging", errorState)) {
            updatedErrorState = removeErrorState(
              "add_product_packaging",
              updatedErrorState,
              errorState
            );
            updateErrorStates(updatedErrorState);
          }
        }
      }
      if (!fieldName || fieldName === "add_product_manufacturer") {
        if ( (_formState?.manufacturer?.label||"").trim().length===0) {
          if (!checkErrorStateExists("add_product_manufacturer", errorState)) {
            updatedErrorState.push({ add_product_manufacturer: true });
          }
          updateErrorStates(updatedErrorState);
          focusInputField("add_product_manufacturer");
          displayToast({
            type: "error",
            title: "Please enter a manufacturer name",
          });
          return false;
        } else {
          if (checkErrorStateExists("add_product_manufacturer", errorState)) {
            updatedErrorState = removeErrorState(
              "add_product_manufacturer",
              updatedErrorState,
              errorState
            );
            updateErrorStates(updatedErrorState);
          }
        }
      }
      if (!fieldName || fieldName === "add_product_gst") {
        if (!_formState?.gst) {
          if (!checkErrorStateExists("add_product_gst", errorState)) {
            updatedErrorState.push({ add_product_gst: true });
          }
          updateErrorStates(updatedErrorState);
          focusInputField("add_product_gst");
          displayToast({
            type: "error",
            title: "Please select a valid GST option",
          });
          return false;
        } else {
          if (checkErrorStateExists("add_product_gst", errorState)) {
            updatedErrorState = removeErrorState(
              "add_product_gst",
              updatedErrorState,
              errorState
            );
            updateErrorStates(updatedErrorState);
          }
        }
      }
      if (!fieldName || fieldName === "add_product_schedule_type") {
        if (!_formState?.scheduleType) {
          if (!checkErrorStateExists("add_product_schedule_type", errorState)) {
            updatedErrorState.push({ add_product_schedule_type: true });
          }
          updateErrorStates(updatedErrorState);
          focusInputField("add_product_schedule_type");
          displayToast({
            type: "error",
            title: "Please select a schedule type",
          });
          return false;
        } else {
          if (checkErrorStateExists("add_product_schedule_type", errorState)) {
            updatedErrorState = removeErrorState(
              "add_product_schedule_type",
              updatedErrorState,
              errorState
            );
            updateErrorStates(updatedErrorState);
          }
        }
      }
      if (!fieldName || fieldName === "add_product_hsn_code") {
        if (
          !_formState?.hsnCode ||
          _formState?.hsnCode.toString().length < 6 ||
          _formState?.hsnCode.toString().length > 8
        ) {
          if (!checkErrorStateExists("add_product_hsn_code", errorState)) {
            updatedErrorState.push({ add_product_hsn_code: true });
          }
          updateErrorStates(updatedErrorState);
          focusInputField("add_product_hsn_code");
          displayToast({
            type: "error",
            title: "Please enter a valid HSN code",
          });
          return false;
        } else {
          if (checkErrorStateExists("add_product_hsn_code", errorState)) {
            updatedErrorState = removeErrorState(
              "add_product_hsn_code",
              updatedErrorState,
              errorState
            );
            updateErrorStates(updatedErrorState);
          }
        }
      }
      if (!fieldName || fieldName === "add_product_qty_per_unit") {
        if (!_formState?.divisor) {
          if (!checkErrorStateExists("add_product_qty_per_unit", errorState)) {
            updatedErrorState.push({ add_product_qty_per_unit: true });
          }
          updateErrorStates(updatedErrorState);
          focusInputField("add_product_qty_per_unit");
          displayToast({
            type: "error",
            title: "Please select a packaging qty",
          });
          return false;
        } else {
          if (checkErrorStateExists("add_product_qty_per_unit", errorState)) {
            updatedErrorState = removeErrorState(
              "add_product_qty_per_unit",
              updatedErrorState,
              errorState
            );
            updateErrorStates(updatedErrorState);
          }
        }
      }
      if (
        (_formState?.rateItemwise && !fieldName) ||
        fieldName === "add_product_selling_price"
      ) {
        if (!_formState?.sellingPrice) {
          if (!checkErrorStateExists("add_product_selling_price", errorState)) {
            updatedErrorState.push({ add_product_selling_price: true });
          }
          updateErrorStates(updatedErrorState);
          focusInputField("add_product_selling_price");
          displayToast({
            type: "error",
            title: "Please enter a selling price.",
          });
          return false;
        } else {
          if (checkErrorStateExists("add_product_selling_price", errorState)) {
            updatedErrorState = removeErrorState(
              "add_product_selling_price",
              updatedErrorState,
              errorState
            );
            updateErrorStates(updatedErrorState);
          }
        }
      }

      if (!fieldName || fieldName === "add_product_min_stock") {
        if (
          (_formState?.minStock < 1 && _formState?.minStock != null) ||
          (!_formState?.minStock && _formState?.maxStock)
        ) {
          if (!checkErrorStateExists("add_product_min_stock", errorState)) {
            updatedErrorState.push({ add_product_min_stock: true });
          }
          updateErrorStates(updatedErrorState);
          focusInputField("add_product_min_stock");
          displayToast({
            type: "error",
            title: "Please enter a Min stock value greater than Zero.",
          });
          return false;
        } else {
          if (checkErrorStateExists("add_product_min_stock", errorState)) {
            updatedErrorState = removeErrorState(
              "add_product_min_stock",
              updatedErrorState,
              errorState
            );
            updateErrorStates(updatedErrorState);
          }
        }
      }

      if (!fieldName || fieldName === "add_product_max_stock") {
        if (
          (_formState?.maxStock < 1 && _formState?.maxStock != null) ||
          (_formState?.minStock && !_formState?.maxStock)
        ) {
          if (!checkErrorStateExists("add_product_max_stock", errorState)) {
            updatedErrorState.push({ add_product_max_stock: true });
          }
          updateErrorStates(updatedErrorState);
          focusInputField("add_product_max_stock");
          displayToast({
            type: "error",
            title: "Please enter a Max stock value greater than Zero.",
          });
          return false;
        } else {
          if (checkErrorStateExists("add_product_max_stock", errorState)) {
            updatedErrorState = removeErrorState(
              "add_product_max_stock",
              updatedErrorState,
              errorState
            );
            updateErrorStates(updatedErrorState);
          }
        }
      }

      if (!fieldName || fieldName === "add_product_max_stock") {
        if (
          _formState?.minStock > _formState?.maxStock &&
          _formState?.minStock &&
          _formState?.maxStock
        ) {
          if (!checkErrorStateExists("add_product_max_stock", errorState)) {
            updatedErrorState.push({ add_product_max_stock: true });
          }
          updateErrorStates(updatedErrorState);
          focusInputField("add_product_max_stock");
          displayToast({
            type: "error",
            title: "Max stock cannot be less than min stock",
          });
          return false;
        } else {
          if (checkErrorStateExists("add_product_max_stock", errorState)) {
            updatedErrorState = removeErrorState(
              "add_product_max_stock",
              updatedErrorState,
              errorState
            );
            updateErrorStates(updatedErrorState);
          }
        }
      }
      if (!fieldName || fieldName === "add_product_min_stock") {
        if (
          _formState?.minStock > _formState?.maxStock &&
          _formState?.minStock &&
          _formState?.maxStock
        ) {
          if (!checkErrorStateExists("add_product_min_stock", errorState)) {
            updatedErrorState.push({ add_product_min_stock: true });
          }
          updateErrorStates(updatedErrorState);
          focusInputField("add_product_min_stock");
          displayToast({
            type: "error",
            title: "Min stock cannot be greater than max stock",
          });
          return false;
        } else {
          if (checkErrorStateExists("add_product_min_stock", errorState)) {
            updatedErrorState = removeErrorState(
              "add_product_min_stock",
              updatedErrorState,
              errorState
            );
            updateErrorStates(updatedErrorState);
          }
        }
      }

      if (!fieldName || fieldName === "add_product_max_order_days") {
        if (
          (_formState?.minDays && !_formState?.maxDays) ||
          (_formState?.maxDays < 1 && _formState?.maxDays != null)
        ) {
          if (
            !checkErrorStateExists("add_product_max_order_days", errorState)
          ) {
            updatedErrorState.push({ add_product_max_order_days: true });
          }
          updateErrorStates(updatedErrorState);
          focusInputField("add_product_max_order_days");
          displayToast({
            type: "error",
            title: "Please enter Min and Max days value greater than Zero.",
          });
          return false;
        } else {
          if (checkErrorStateExists("add_product_max_order_days", errorState)) {
            updatedErrorState = removeErrorState(
              "add_product_max_order_days",
              updatedErrorState,
              errorState
            );
            updateErrorStates(updatedErrorState);
          }
        }
      }

      if (!fieldName || fieldName === "add_product_min_order_days") {
        if (
          (_formState?.minDays < 1 && _formState?.minDays != null) ||
          (!_formState?.minDays && _formState?.maxDays)
        ) {
          if (
            !checkErrorStateExists("add_product_min_order_days", errorState)
          ) {
            updatedErrorState.push({ add_product_min_order_days: true });
          }
          updateErrorStates(updatedErrorState);
          focusInputField("add_product_min_order_days");
          displayToast({
            type: "error",
            title: "Please enter Min and Max days value greater than Zero.",
          });
          return false;
        } else {
          if (checkErrorStateExists("add_product_min_order_days", errorState)) {
            updatedErrorState = removeErrorState(
              "add_product_min_order_days",
              updatedErrorState,
              errorState
            );
            updateErrorStates(updatedErrorState);
          }
        }
      }

      if (!fieldName || fieldName === "add_product_max_order_days") {
        if (
          _formState?.minDays > _formState?.maxDays &&
          _formState?.minDays &&
          _formState?.maxDays
        ) {
          if (
            !checkErrorStateExists("add_product_max_order_days", errorState)
          ) {
            updatedErrorState.push({ add_product_max_order_days: true });
          }
          updateErrorStates(updatedErrorState);
          focusInputField("add_product_max_order_days");
          displayToast({
            type: "error",
            title: "Max days cannot be less than min days.",
          });
          return false;
        } else {
          if (checkErrorStateExists("add_product_max_order_days", errorState)) {
            updatedErrorState = removeErrorState(
              "add_product_max_order_days",
              updatedErrorState,
              errorState
            );
            updateErrorStates(updatedErrorState);
          }
        }
      }

      if (!fieldName || fieldName === "add_product_min_order_days") {
        if (
          _formState?.minDays > _formState?.maxDays &&
          _formState?.minDays &&
          _formState?.maxDays
        ) {
          if (
            !checkErrorStateExists("add_product_min_order_days", errorState)
          ) {
            updatedErrorState.push({ add_product_min_order_days: true });
          }
          updateErrorStates(updatedErrorState);
          focusInputField("add_product_min_order_days");
          displayToast({
            type: "error",
            title: "Min days cannot be greater than max days.",
          });
          return false;
        } else {
          if (checkErrorStateExists("add_product_min_order_days", errorState)) {
            updatedErrorState = removeErrorState(
              "add_product_min_order_days",
              updatedErrorState,
              errorState
            );
            updateErrorStates(updatedErrorState);
          }
        }
      }

      if (!fieldName || fieldName === "add_product_auto_order_days") {
        if (
          (_formState?.autoOrderDays < 1 &&
            _formState?.autoOrderDays != null) ||
          (_formState?.minDays &&
            _formState?.maxDays &&
            !_formState?.autoOrderDays)
        ) {
          if (
            !checkErrorStateExists("add_product_auto_order_days", errorState)
          ) {
            updatedErrorState.push({ add_product_auto_order_days: true });
          }
          updateErrorStates(updatedErrorState);
          focusInputField("add_product_auto_order_days");
          displayToast({
            type: "error",
            title: "Please enter auto order days greater than zero.",
          });
          return false;
        } else {
          if (
            checkErrorStateExists("add_product_auto_order_days", errorState)
          ) {
            updatedErrorState = removeErrorState(
              "add_product_auto_order_days",
              updatedErrorState,
              errorState
            );
            updateErrorStates(updatedErrorState);
          }
        }
      }

      if (!fieldName || fieldName === "add_product_auto_order_days") {
        if (_formState?.computeMinMax && !_formState?.autoOrderDays) {
          if (
            !checkErrorStateExists("add_product_auto_order_days", errorState)
          ) {
            updatedErrorState.push({ add_product_auto_order_days: true });
          }
          updateErrorStates(updatedErrorState);
          focusInputField("add_product_auto_order_days");
          displayToast({
            type: "error",
            title: "Please enter auto order days greater than zero.",
          });
          return false;
        } else {
          if (
            checkErrorStateExists("add_product_auto_order_days", errorState)
          ) {
            updatedErrorState = removeErrorState(
              "add_product_auto_order_days",
              updatedErrorState,
              errorState
            );
            updateErrorStates(updatedErrorState);
          }
        }
      }

      if (!fieldName || fieldName === "add_product_b2b_force_selling_price") {
        if (
          _formState?.b2bForceSellingPrice &&
          (_formState?.b2bSellingPrice === null ||
            _formState?.b2bSellingPrice === undefined ||
            _formState?.b2bSellingPrice === "")
        ) {
          if (
            !checkErrorStateExists(
              "add_product_sales_b2b_selling_price",
              errorState
            )
          ) {
            updatedErrorState.push({
              add_product_sales_b2b_selling_price: true,
            });
          }
          updateErrorStates(updatedErrorState);
          focusInputField("add_product_sales_b2b_selling_price");
          displayToast({
            type: "error",
            title: "Please enter a B2B selling price.",
          });
          return false;
        } else {
          if (
            checkErrorStateExists(
              "add_product_sales_b2b_selling_price",
              errorState
            )
          ) {
            updatedErrorState = removeErrorState(
              "add_product_sales_b2b_selling_price",
              updatedErrorState,
              errorState
            );
            updateErrorStates(updatedErrorState);
          }
        }
      }

      return true;
    },
    [errorState, updateErrorStates]
  );

  const debounceValidateForm = useCallback(_.debounce(validateForm, 300), []);

  const updateIsAddIngredients = useCallback((value) => {
    setIsAddIngredients(value);
  }, []);

  const addIngredients = async () => {
    if (validateForm(null, formState)) {
      updateIsAddIngredients(true);
    }
  };

  const getIngredients = useCallback((ingredients = []) => {
    ingredients.forEach((ingredient) => {
      getMedicineDetails(`medicineId=${ingredient.itemMedicineId}`).then(
        (resp) => {
          setIngredients((prev) => {
            return [
              ...prev,
              {
                name: {
                  label: resp.medicineNameDetailed,
                  id: resp.medicineId,
                },
                quantity: ingredient.quantity,
                loose: ingredient.loose,
                medicinePackaging: resp.medicinePackaging,
                itemMedicineId: ingredient.itemMedicineId,
                recipeItemId: ingredient.recipeItemId,
              },
            ];
          });
        }
      );
    });
  }, []);

  const saveFormHandler = useCallback(async () => {
    if (validateForm(null, formState)) {
      setIsSaving(true);
      const productNameDetailed =
        formState?.productName +
        " " +
        formState?.itemType.label +
        " " +
        formState?.medicinePackaging;
      const productNameNospace = productNameDetailed.replaceAll(/\W|_/g, "");

      const localFormState = createFormState({
        productNameDetailed,
        productNameNospace,
        productName: formState?.productName,
        productBrand: formState?.productBrand,
        productId: formState?.productId,
        itemType: formState?.itemType,
        category: formState?.category?.value,
        medicinePackaging: formState?.medicinePackaging,
        therapeutic: formState?.therapeutic,
        subTherapeutic: formState?.subTherapeutic,
        barcode: formState?.barcode,
        medicineShortCode: formState?.medicineShortCode,
        manufacturer: formState?.manufacturer,
        manufacturerGroupName: formState?.manufacturer?.manufacturerGroupName,
        gst: formState?.gst,
        minStock: formState?.minStock,
        maxStock: formState?.maxStock,
        minDays: formState?.minDays,
        maxDays: formState?.maxDays,
        hsnCode: formState?.hsnCode,
        rack: formState?.rack,
        rack1: formState?.rack1,
        remarks: formState?.remarks,
        isTb: formState?.isTb,
        disableAutoBarcodePrinting: formState?.disableAutoBarcodePrinting,
        isLoose: formState?.isLoose,
        divisor: formState?.divisor,
        description: formState?.description,
        scheduleType: formState?.scheduleType,
        genericDosage: formState?.genericDosage,
        //divisorUnit: formState?.divisorUnit,
        discPer: formState?.discPer || null,
        maxDiscount: formState?.maxDiscount || null,
        tcsPer: formState?.tcsPer,
        gccess: formState?.gccess,
        vat: formState?.vat,
        boxPtr: formState?.boxPtr,
        boxMrp: formState?.boxMrp,
        markup: formState?.markup,
        passiveMrp: formState?.passiveMrp,
        passiveCp: formState?.passiveCp,
        isBanned: formState?.isBanned,
        isDiscontinued: formState?.isDiscontinued,
        isUndeliverable: formState?.isUndeliverable,
        isActive: formState?.isActive,
        skipFromApo: formState?.skipFromApo,
        skipFromShortBook: formState?.skipFromShortBook,
        blockExpiryPrint: formState?.blockExpiryPrint,
        allowWeb: formState?.allowWeb,
        storage: formState?.storage?.label,
        isChronic: formState?.isChronic,
        outerQtyPack: formState?.outerQtyPack,
        marketer: formState?.marketer,
        allowDiscount: formState?.allowDiscount,
        isReturnable: formState?.isReturnable,
        sellingPrice: formState?.sellingPrice,
        rateItemwise: formState?.rateItemwise,
        isRecipe: isAddRecipe,
        recipeItem: [...ingredients],
        autoOrderDays: formState?.autoOrderDays,
        computeMinMax: formState?.computeMinMax,
        b2BOffset: formState?.b2BOffset ?? null,
        b2bSellingPrice: formState?.b2bSellingPrice ?? null,
        b2bForceSellingPrice: formState?.b2bForceSellingPrice,
      });

      const payload = {
        itemGroupName: formState?.itemType.label.toUpperCase(),
      };
      // if (formState?.itemType.id === "custom") {
      //   await saveItemType(payload).then(async (resp) => {
      //     if (resp?.status === "success") {
      //       const response = await searchItemType();
      //       const updatedResponse = response?.rows.map((item) => {
      //         return {
      //           id: item.itemGroupId,
      //           label: item.itemGroupName,
      //         };
      //       });
      //       dispatch({
      //         type: "update_item_type_options",
      //         payload: updatedResponse,
      //       });
      //     }
      //   });
      // }
      const cleanFromState = cleanObject(localFormState);
      await saveMedicine(cleanFromState)
        .then((response) => {
          if (response.status !== "success") {
            displayToast({
              type: "error",
              title: response,
            });
            setIsSaving(false);
            return;
          }
          displayToast({
            type: "success",
            title: "Product updated successfully",
          });
          onSave(response.result);
          setShowAddProductAutocomplete(false);
        })
        .catch(() => {
          setIsSaving(false);
        });
    }
  }, [
    formState,
    ingredients,
    isAddRecipe,
    onSave,
    setShowAddProductAutocomplete,
    validateForm,
  ]);

  const debounceAndSave = debounce(saveFormHandler, 300);

  const debounceAndAddIngredients = debounce(addIngredients, 300);

  const saveRecipeHandler = useCallback(() => {
    const _ingredients = [...ingredients];
    const valid = _ingredients.every((i) => {
      if (!i.name?.label) {
        displayToast({
          type: "error",
          title: "Please select Ingredient",
        });
        return false;
      }
      if (i.quantity === 0 && i.loose === 0) {
        displayToast({
          type: "error",
          title: "Please enter quantity or loose",
        });
        return false;
      }
      return true;
    });

    if (valid) debounceAndSave();
  }, [debounceAndSave, ingredients]);

  const deleteIngredientsHandler = useCallback(
    (index) => {
      const _ingredients = [...ingredients];
      const filteredIngredients = _ingredients.filter((_, i) => i !== index);
      setIngredients(filteredIngredients);
      setTimeout(() => {
        let newIndex = index;
        if (newIndex === filteredIngredients.length) {
          newIndex -= 1;
        }
        document
          .getElementById(`row_item_0_${newIndex}`)
          ?.focus({ preventScroll: true });
      }, 100);
    },
    [ingredients]
  );

  const resetForm = useCallback(() => {
    dispatch({
      type: "reset_add_product_form_state",
    });
    updateErrorStates([]);
    resetIngredients();
  }, [dispatch, updateErrorStates, resetIngredients]);

  const escapeHandler = useCallback(() => {
    onEscape();
    resetForm();
  }, [onEscape, resetForm]);

  const keyupListener = useCallback(
    (e) => {
      if (isSaving) return;
      const key = e.key?.toLowerCase();
      const altKey = e.altKey;
      const ctrlKey = e.ctrlKey;
      if (altKey) {
        if ([1, 2, 3].includes(+key)) {
          setFocusTab(+key - 1);
          return;
        }
      }
      if (key === "escape") {
        escapeHandler();
        return;
      }
      if (ctrlKey && key === "s") {
        addProductButtonRef.current.click();
        return;
      }
    },
    [escapeHandler, isSaving]
  );

  const addIngredientsKeyupListener = useCallback(
    (e) => {
      e.preventDefault();
      const key = e.key.toLowerCase();
      const ctrlKey = e.ctrlKey;
      if (ctrlKey && key === "s") {
        return saveRecipeButtonRef.current?.click();
      }
      if (key === "+") {
        return addIngredientsButtonRef.current?.click();
      }
      if (key === "escape") {
        backButtonRef.current?.click();
        return;
      }
    },
    [addIngredientsButtonRef, saveRecipeButtonRef, backButtonRef]
  );

  const getPath = useCallback((category, categoryId, parents = []) => {
    if (category.categoryId === categoryId) {
      parents.push(category.categoryName);
      setPath([...parents]);
      return parents;
    }
    if (category.childs && category.childs.length > 0) {
      for (let child of category.childs) {
        const result = getPath(child, categoryId, [
          ...parents,
          category.categoryName,
        ]);
        if (result) {
          return result;
        }
      }
    }
    return null;
  }, []);

  const removeErrorStateOnChange = useCallback(
    (id) => {
      let updatedErrorState = errorState;
      if (checkErrorStateExists(id, errorState)) {
        updatedErrorState = removeErrorState(id, updatedErrorState, errorState);
        updateErrorStates(updatedErrorState);
      }
    },
    [updateErrorStates, errorState]
  );

  const tabs = useMemo(() => {
    return [
      {
        type: ADD_PRODUCT_GENERAL_TAB,
        label: "General",
        showCloseIcon: false,
        props: {
          editProductId,
          isProductSearchable,
          removeErrorStateOnChange,
          resetForm,
          validateForm: debounceValidateForm,
          isAddIngredients,
          getIngredients,
          showAddProduct,
          categoryRef,
          resetIngredients,
        },
        endAvatar: <Shortcut keys={["Alt", "1"]} />,
      },
      {
        type: ADD_PRODUCT_STOCK_OR_PRICES_TAB,
        label: "Stock / Prices",
        showCloseIcon: false,
        props: {
          removeErrorStateOnChange,
          validateForm: debounceValidateForm,
          isAddIngredients,
        },
        endAvatar: <Shortcut keys={["Alt", "2"]} />,
      },
      {
        type: ADD_PRODUCT_MISCELLANEOUS_TAB,
        label: "Miscellaneous",
        showCloseIcon: false,
        props: {
          removeErrorStateOnChange,
          isAddIngredients,
        },
        endAvatar: <Shortcut keys={["Alt", "3"]} />,
      },
    ];
  }, [
    editProductId,
    isProductSearchable,
    removeErrorStateOnChange,
    resetForm,
    debounceValidateForm,
    isAddIngredients,
    getIngredients,
    showAddProduct,
    resetIngredients,
  ]);

  useEffect(() => {
    resetForm();
  }, [resetForm]);

  useEffect(() => {
    if (!isAddIngredients) {
      document.addEventListener("keyup", keyupListener, false);
      return () => {
        document.removeEventListener("keyup", keyupListener, false);
      };
    }
  }, [isAddIngredients, keyupListener]);

  useEffect(() => {
    if (isAddIngredients) {
      document.addEventListener("keyup", addIngredientsKeyupListener, false);
      return () => {
        document.removeEventListener(
          "keyup",
          addIngredientsKeyupListener,
          false
        );
      };
    }
  }, [addIngredientsKeyupListener, isAddIngredients]);

  useEffect(() => {
    if (isAddIngredients && ingredients.length === 0) {
      setIngredients([
        {
          name: "",
          quantity: 0,
          loose: 0,
        },
      ]);
    }
  }, [ingredients.length, isAddIngredients]);

  useEffect(() => {
    if (!newProduct) return;
    dispatch({
      type: "set_add_product_form_state",
      payload: {
        productName: newProduct,
      },
    });
  }, [newProduct, dispatch]);

  useEffect(() => {
    return () => {
      debounceValidateForm.cancel();
      resetForm();
    };
  }, [resetForm, debounceValidateForm]);

  useEffect(() => {
    getCategory().then((response) => {
      response.some((category) => {
        return getPath(category, formState?.category?.value?.categoryId);
      });
    });
  }, [categoryPath, formState?.category?.value?.categoryId, getPath]);
  return (
    <div className={classes["add-product-container"]}>
      <div className={classes["add-product-header"]}>
        <div className={classes["add-product-header-title"]}>
          {`${formState?.productName ? formState?.productName : ""} ${
            formState?.itemType?.label ? formState?.itemType.label : ""
          } ${
            formState?.medicinePackaging ? formState?.medicinePackaging : ""
          }${formState?.productId ? " [" + formState?.productId + "]" : ""}`}
          {categoryPath && path.length > 1 && (
            <div className={classes["catgeory-hierarchy"]}>
              <Typography fontSize={13} fontWeight={600}>
                Category Hierarchy:
              </Typography>
              <Typography fontSize={13}>{path.join("/")}</Typography>
            </div>
          )}
        </div>

        <div className={classes["add-product-header-right"]}>
          <div className={classes["add-product-header-info"]}>
            <Typography
              fontSize={15}
              fontWeight={800}
              sx={{
                whiteSpace: "nowrap",
              }}
            >
              Last Saved Product:
            </Typography>
            <Typography
              fontSize={15}
              fontWeight={800}
              sx={{ whiteSpace: "nowrap", textTransform: "capitalize" }}
            >
              {productDetails.medicineName}
            </Typography>
            <div className={classes["medicine-details"]}>
              <Typography fontSize={15} fontWeight={800}>
                [{productDetails.medicineId}]
              </Typography>
            </div>
          </div>
          <div className={classes["add-product-header-shortcuts"]}>
            <Shortcut keys={["Esc"]} label="Cancel" />
          </div>
        </div>
      </div>
      <div className={classes["add-product-tabs"]}>
        <Tab
          tabs={tabs}
          focusTabIndex={focusTab}
          showCloseIcon={false}
          tabClicked={(index) => {
            setFocusTab(index);
          }}
        />
      </div>
      <Slide
        direction="left"
        in={isAddIngredients}
        mountOnEnter
        unmountOnExit
        timeout={100}
      >
        <div className={classes["add-ingredients-container"]}>
          <div className={classes["add-ingredients-subcontainer"]}>
            <div style={{ height: "auto", maxHeight: "85%", width: "100%" }}>
              <div className={classes["add-ingredients-title-container"]}>
                <div className={classes["add-ingredients-title"]}>
                  Add Ingredients
                </div>
                <Button
                  label="ADD"
                  color="black"
                  ref={addIngredientsButtonRef}
                  onClick={() => {
                    setIngredients((prev) => {
                      return [
                        ...prev,
                        {
                          name: "",
                          quantity: 0,
                          loose: 0,
                        },
                      ];
                    });
                  }}
                  endIcon={<Shortcut keys={["+"]} />}
                />
              </div>
              <div className={classes["add-ingredients-details-container"]}>
                <div className={classes["add-ingredients-details"]}>
                  {ingredients?.map((ingredient, index) => {
                    return (
                      <AddIngredients
                        key={`${index}_${ingredient.name}`}
                        index={index}
                        ingredient={ingredient}
                        showDeleteIcon={showDeleteIcon}
                        onDeleteIngredient={deleteIngredientsHandler}
                        onChange={(key, data) => {
                          setIngredients((prev) => {
                            prev[index][key] = data;
                            return [...prev];
                          });
                        }}
                      />
                    );
                  })}
                </div>
              </div>
            </div>
            <div className={classes["add-ingredients-cta-container"]}>
              <Button
                label="Back"
                ref={backButtonRef}
                onClick={() => {
                  updateIsAddIngredients(false);
                }}
                endIcon={<Shortcut keys={["Esc"]} />}
              />
              <Button
                label="Save Recipe"
                ref={saveRecipeButtonRef}
                onClick={saveRecipeHandler}
                color="violet"
                endIcon={<Shortcut keys={["Ctrl", "S"]} />}
              />
            </div>
          </div>
        </div>
      </Slide>
      <div className={classes["add-product-button-container"]}>
        <Button
          label={isAddRecipe ? "Add Ingredients" : "Save Product"}
          color="violet"
          ref={addProductButtonRef}
          endIcon={<Shortcut keys={["Ctrl", "S"]} />}
          onFocus={() => setCurrentComponent("add_product_save_button")}
          isAccessible={!isAddIngredients}
          disabled={isSaving}
          onClick={isAddRecipe ? debounceAndAddIngredients : debounceAndSave}
        />
      </div>
    </div>
  );
};

export default AddProduct;
